import { useState } from "react";
import axios from "axios";
import { getSessionFromCookie } from "../components/utils/cognito";
import { RootState } from "../store/indexRedux";
import { useSelector } from "react-redux";

interface CouponResponse {
  status: string;
  data: {
    couponCode: string;
    partnerId: number;
    contactId: number;
    generatedBy: string;
    userRole: string;
    generatedAt: string;
  };
}

function GeneratePartnerCoupon() {
  const [couponData, setCouponData] = useState<CouponResponse["data"] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userInfo = useSelector((state: RootState) => state.user.user);

  const session = getSessionFromCookie();
  const token = session?.getAccessToken().getJwtToken();

  // Generate the referral link
  const getReferralLink = () => {
    if (!couponData) return "";
    const baseUrl = window.location.origin; // This will get http://localhost:3002 or your production URL
    return `${baseUrl}/?ref=${couponData.couponCode}`;
  };

  const generateCoupon = async () => {
    setIsLoading(true);
    setError(null);
    setCopied(false);
    setLinkCopied(false);

    try {
      const response = await axios.get<CouponResponse>(
        `${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/partners/coupon/generate`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        setCouponData(response.data.data);
      }
    } catch (err) {
      console.error("Error generating coupon:", err);
      setError("Failed to generate coupon. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const copyCouponCode = async () => {
    if (!couponData) return;

    try {
      await navigator.clipboard.writeText(couponData.couponCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const copyReferralLink = async () => {
    if (!couponData) return;

    try {
      await navigator.clipboard.writeText(getReferralLink());
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-primary-950">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-primary-700 dark:text-white mb-3">Partner Referral Coupons</h1>
          <p className="text-primary-500 dark:text-gray-400 max-w-2xl mx-auto">
            Generate unique referral codes for your clients. Share these codes to allow them to join your partner
            network seamlessly.
          </p>
        </div>

        {!couponData ? (
          // Initial state - Generate button
          <div className="bg-white dark:bg-primary-900 rounded-xl shadow-sm border border-gray-100 dark:border-primary-800 p-12">
            <div className="text-center">
              <div className="mb-8">
                <div className="w-20 h-20 bg-primary-100 dark:bg-primary-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-10 h-10 text-primary-600 dark:text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-primary-700 dark:text-white mb-2">
                  Ready to Generate a Partner Coupon?
                </h2>
                <p className="text-primary-500 dark:text-gray-400 text-sm">
                  Click below to create a unique referral code for your client
                </p>
              </div>

              <button
                onClick={generateCoupon}
                disabled={isLoading}
                className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Generate Partner Coupon
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Coupon display state
          <div className="space-y-6">
            <div className="bg-white dark:bg-primary-900 rounded-xl shadow-sm border border-gray-100 dark:border-primary-800 overflow-hidden">
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-primary-100 text-sm font-medium mb-1">Partner Referral Code</p>
                    <h2 className="text-white text-2xl font-bold">Successfully Generated!</h2>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Coupon Code Section */}
                <div className="bg-gray-50 dark:bg-primary-800 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-primary-500 dark:text-gray-400">Coupon Code</span>
                    <span className="text-xs text-primary-400 dark:text-gray-500">
                      {formatDate(couponData.generatedAt)}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="relative">
                        <div className="font-mono text-3xl font-bold text-primary-700 dark:text-white tracking-wider">
                          {couponData.couponCode}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={copyCouponCode}
                      className={`
                        relative px-4 py-3 rounded-lg font-medium transition-all
                        ${
                          copied
                            ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                            : "bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-700"
                        }
                      `}
                    >
                      {copied ? (
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                          Copy Code
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {/* Referral Link Section */}
                <div className="bg-primary-50 dark:bg-primary-900/50 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">Referral Link</span>
                    <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-white dark:bg-primary-800 rounded-md px-3 py-2 border border-primary-200 dark:border-primary-700">
                      <p className="text-sm text-primary-600 dark:text-primary-300 truncate font-mono">
                        {getReferralLink()}
                      </p>
                    </div>

                    <button
                      onClick={copyReferralLink}
                      className={`
                        px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap
                        ${
                          linkCopied
                            ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                            : "bg-primary-600 text-white hover:bg-primary-700"
                        }
                      `}
                    >
                      {linkCopied ? (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                          Copy Link
                        </div>
                      )}
                    </button>
                  </div>

                  <p className="text-xs text-primary-500 dark:text-gray-400 mt-2">
                    Share this link directly with your clients for automatic coupon application
                  </p>
                </div>

                {/* User Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-primary-900/50 rounded-lg p-4">
                    <p className="text-xs text-primary-500 dark:text-gray-400 mb-1">Generated By</p>
                    <p className="font-medium text-primary-700 dark:text-white">
                      {userInfo?.firstName} {userInfo?.lastName}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-primary-900/50 rounded-lg p-4">
                    <p className="text-xs text-primary-500 dark:text-gray-400 mb-1">Email</p>
                    <p className="font-medium text-primary-700 dark:text-white text-sm truncate">{userInfo?.email}</p>
                  </div>
                </div>

                {/* Instructions Section */}
                <div className="border-t dark:border-primary-800 pt-6">
                  <div className="flex items-start gap-3 mb-6">
                    <svg
                      className="w-5 h-5 text-primary-400 dark:text-gray-500 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-medium text-primary-700 dark:text-white mb-2">How to share</h3>
                      <ul className="text-sm text-primary-500 dark:text-gray-400 space-y-1">
                        <li>
                          • Share the <strong>coupon code</strong> for manual entry during registration
                        </li>
                        <li>
                          • Share the <strong>referral link</strong> for automatic coupon application
                        </li>
                        <li>• The link will pre-fill the coupon code in the registration form</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    {/* <button
                      onClick={generateCoupon}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Generate New Code
                    </button> */}

                    <button
                      disabled
                      className="flex-1 bg-gray-100 dark:bg-primary-800 text-gray-400 dark:text-gray-500 font-semibold py-2.5 px-4 rounded-lg cursor-not-allowed inline-flex items-center justify-center gap-2"
                      title="Coming soon"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      Send via Email (Coming Soon)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GeneratePartnerCoupon;
